import { ChangeEvent, useEffect, useRef, useState } from "react";
import JicFloatingWidget from "./jicFloatingWidget";
import LoveTransferPanel from "./loveTransferPanel";
import { computeLogoHash, resolveLogoOnServer } from "../lib/logoHash";
import { getJicToken } from "../lib/jic";

// logo.jic.io — 全世界的 LOGO 都可以成为锚点。扫 LOGO → 送 LOVE。
type Screen = "home" | "scanning" | "matched";

type LogoMatch = { slug: string; label: string; address?: string; confidence: number; mode: string };

export default function LogoScanner() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const enrollRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const busyRef = useRef(false);
  const [screen, setScreen] = useState<Screen>("home");
  const [message, setMessage] = useState("");
  const [match, setMatch] = useState<LogoMatch | null>(null);
  const [logoCount, setLogoCount] = useState<number | null>(null);
  const [enrollSlug, setEnrollSlug] = useState("");
  const [enrollMsg, setEnrollMsg] = useState("");

  const stopScanner = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    resolveLogoOnServer("0".repeat(64)).then((r) => setLogoCount(r?.count ?? null));
    return stopScanner;
  }, []);

  const resolveFrame = async (input: HTMLImageElement | HTMLVideoElement) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    try {
      const hash = computeLogoHash(input);
      if (!hash) {
        setMessage("读不了这张图");
        return false;
      }
      const result = await resolveLogoOnServer(hash);
      if (result?.match) {
        setMatch({
          slug: result.match.slug,
          label: result.match.label,
          address: result.match.address || undefined,
          confidence: result.match.confidence,
          mode: result.match.mode,
        });
        setScreen("matched");
        stopScanner();
        return true;
      }
      setMessage("还没认出来,把 LOGO 摆正、占满画面");
      return false;
    } finally {
      busyRef.current = false;
    }
  };

  const startCamera = async () => {
    stopScanner();
    setMatch(null);
    setScreen("scanning");
    setMessage("正在打开相机…");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (!videoRef.current) throw new Error("相机打不开");
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setMessage("对准一个 LOGO…");
      timerRef.current = window.setInterval(() => {
        if (videoRef.current && videoRef.current.readyState >= 2) void resolveFrame(videoRef.current);
      }, 800);
    } catch (error) {
      setScreen("home");
      setMessage(error instanceof Error ? error.message : "相机打不开");
      stopScanner();
    }
  };

  const fromFile = (file: File, onLoaded: (image: HTMLImageElement) => void) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      onLoaded(image);
      URL.revokeObjectURL(objectUrl);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setMessage("图片打不开");
      setScreen("home");
    };
    image.src = objectUrl;
  };

  const onPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopScanner();
    setMatch(null);
    setScreen("scanning");
    setMessage("正在认这个 LOGO…");
    fromFile(file, async (image) => {
      const hit = await resolveFrame(image);
      if (!hit) setScreen("home");
      event.target.value = "";
    });
  };

  // 策展人录入:选一个自己创世的 slug + 上传 LOGO 原图
  const onEnroll = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !enrollSlug.trim()) return;
    setEnrollMsg("入库中…");
    fromFile(file, async (image) => {
      const hash = computeLogoHash(image);
      if (!hash) {
        setEnrollMsg("读不了这张图");
        return;
      }
      const response = await fetch(`https://u.jic.io/v1/face/anchor/${enrollSlug.trim().toLowerCase()}/logohash`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJicToken()}` },
        body: JSON.stringify({ hash }),
      });
      const data = (await response.json()) as { ok?: boolean; anchor?: { logos: number }; error?: string };
      setEnrollMsg(
        data.ok
          ? `✓ 已录入(该锚点共 ${data.anchor?.logos} 个 LOGO)`
          : data.error === "curator or admin only"
          ? "只有这个锚点的创建者能录 LOGO(先在 face.jic.io/create 创建)"
          : data.error === "no such anchor"
          ? "没有这个锚点,先去 face.jic.io/create 创建"
          : "没存上"
      );
      event.target.value = "";
    });
  };

  const cancel = () => {
    stopScanner();
    setScreen("home");
    setMessage("");
  };

  return (
    <main className="fs-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />

      {screen === "home" && (
        <section className="fs-home">
          <div className="fs-brand"><span>◈</span> LOGO · JIC</div>
          <h1 className="fs-title">扫 LOGO,送 LOVE</h1>
          <button type="button" className="fs-scan-btn" onClick={startCamera} aria-label="扫一扫">
            <i>◈</i>
            <b>扫一扫</b>
          </button>
          <div className="fs-links">
            <button type="button" className="fs-link" onClick={() => fileRef.current?.click()}>🖼 从相册选 LOGO 图</button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} hidden />
            <a className="fs-link" href="https://face.jic.io/create">⚓ 把一个 LOGO 变成锚点</a>
          </div>
          {message && <p className="fs-msg">{message}</p>}
          <p className="fs-foot">{logoCount === null ? " " : `已有 ${logoCount} 个 LOGO 锚点`}</p>

          <details style={{ marginTop: 20, color: "#5d7482", fontSize: 12 }}>
            <summary style={{ cursor: "pointer" }}>我是锚点创建者,录入 LOGO</summary>
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
              <input
                value={enrollSlug}
                onChange={(event) => setEnrollSlug(event.target.value)}
                placeholder="你的锚点 slug"
                className="fs-big-input"
                style={{ fontSize: 14, padding: "10px 14px" }}
              />
              <button type="button" className="fs-link" onClick={() => enrollRef.current?.click()}>上传 LOGO 原图(截图最佳)</button>
              <input ref={enrollRef} type="file" accept="image/*" onChange={onEnroll} hidden />
              {enrollMsg && <span>{enrollMsg}</span>}
            </div>
          </details>
        </section>
      )}

      {screen !== "home" && (
        <section className="fs-scan">
          <div className={`fs-camera ${screen === "scanning" ? "is-live" : ""}`}>
            <video ref={videoRef} playsInline muted />
            <div className="fs-frame" />
            {screen === "scanning" && <div className="fs-scanline" />}
            {screen === "matched" && match && (
              <div className="fs-hit">
                <span>◈</span>
                <strong>{match.label}</strong>
                <em>{match.confidence}%</em>
              </div>
            )}
          </div>
          {screen === "scanning" && (
            <>
              <p className="fs-msg">{message}</p>
              <button type="button" className="fs-link" onClick={cancel}>✕ 取消</button>
            </>
          )}
          {screen === "matched" && match && (
            <>
              <LoveTransferPanel
                faceId={match.label}
                handle={match.slug}
                recipient={match.address}
                verified={Boolean(match.address)}
              />
              {match.mode === "prereg" && !match.address && (
                <p className="fs-msg">这个 LOGO 在等它的品牌方认领</p>
              )}
              <button type="button" className="fs-link" onClick={cancel}>← 再扫一个</button>
            </>
          )}
        </section>
      )}
      <JicFloatingWidget icon="◈" />
    </main>
  );
}
